import { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from '@/contexts/AuthContext'
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import OwnerRoute from '@/components/auth/OwnerRoute'
import AppLayout from '@/components/layout/AppLayout'
import DevErrorBoundary from '@/components/DevErrorBoundary'
import { lazyWithRetry as lazy } from '@/lib/lazy-with-retry'
import { preloadAllRoutes } from '@/lib/preload-routes'

const Landing = lazy(() => import('@/pages/Landing'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Screen = lazy(() => import('@/pages/Screen'))
// Analysis uses the nested route shell
const AnalysisShell = lazy(() => import('@/pages/analysis/AnalysisShell'))
const AnalysisOverviewTab = lazy(() => import('@/pages/analysis/OverviewTab'))
const AnalysisFinancialsTab = lazy(() => import('@/pages/analysis/FinancialsTab'))
const AnalysisValuationTab = lazy(() => import('@/pages/analysis/ValuationTab'))
const AnalysisCompsTab = lazy(() => import('@/pages/analysis/CompsTab'))
const AnalysisQualitativeTab = lazy(() => import('@/pages/analysis/QualitativeTab'))
const AnalysisRiskTab = lazy(() => import('@/pages/analysis/RiskTab'))
const AnalysisFilingsTab = lazy(() => import('@/pages/analysis/FilingsTab'))
const MoatDetail = lazy(() => import('@/pages/MoatDetail'))
const JobProgress = lazy(() => import('@/pages/JobProgress'))
const Compare = lazy(() => import('@/pages/Compare'))
const ManualInput = lazy(() => import('@/pages/ManualInput'))
const MNA = lazy(() => import('@/pages/MNA'))
const MNAResults = lazy(() => import('@/pages/MNAResults'))
const Analytics = lazy(() => import('@/pages/Analytics'))
const Watchlist = lazy(() => import('@/pages/Watchlist'))
const Events = lazy(() => import('@/pages/Events'))
const Portfolio = lazy(() => import('@/pages/Portfolio'))
const Estimates = lazy(() => import('@/pages/Estimates'))
const Compliance = lazy(() => import('@/pages/Compliance'))
const DevTools = lazy(() => import('@/pages/DevTools'))
const Backtesting = lazy(() => import('@/pages/Backtesting'))
const BatchAnalysis = lazy(() => import('@/pages/BatchAnalysis'))
const FactorAnalytics = lazy(() => import('@/pages/FactorAnalytics'))
const MLInsights = lazy(() => import('@/pages/MLInsights'))
const Strategies = lazy(() => import('@/pages/Strategies'))
const Apophis = lazy(() => import('@/pages/apophis/Apophis'))
const ApophisRequest = lazy(() => import('@/pages/apophis/ApophisRequest'))
const ApophisDelivery = lazy(() => import('@/pages/apophis/ApophisDelivery'))
const ApophisHistory = lazy(() => import('@/pages/apophis/ApophisHistory'))
// Apophis v2 doc-grid pages
const ApophisCatalog = lazy(() => import('@/pages/apophis/ApophisCatalog'))
const ApophisGenerate = lazy(() => import('@/pages/apophis/ApophisGenerate'))
const ApophisJob = lazy(() => import('@/pages/apophis/ApophisJob'))
const ApophisDocuments = lazy(() => import('@/pages/apophis/ApophisDocuments'))
const QuantitativePage = lazy(() => import('@/pages/dashboard/QuantitativePage'))
const CreditRiskPage = lazy(() => import('@/pages/dashboard/CreditRiskPage'))
const InsiderFlowPage = lazy(() => import('@/pages/dashboard/InsiderFlowPage'))
const GovernancePage = lazy(() => import('@/pages/dashboard/GovernancePage'))
const EventTimelinePage = lazy(() => import('@/pages/dashboard/EventTimelinePage'))
const OptionsMarketPage = lazy(() => import('@/pages/dashboard/OptionsMarketPage'))
const MacroOverlayPage = lazy(() => import('@/pages/dashboard/MacroOverlayPage'))
const AttentionSentimentPage = lazy(() => import('@/pages/dashboard/AttentionSentimentPage'))
import DashboardLayout from '@/components/dashboard/DashboardLayout'
const Moat = lazy(() => import('@/pages/Moat'))
const Calibration = lazy(() => import('@/pages/Calibration'))
const CreditUniverse = lazy(() => import('@/pages/CreditUniverse'))
const CrowdedTrades = lazy(() => import('@/pages/CrowdedTrades'))
const Login = lazy(() => import('@/pages/Login'))
const Register = lazy(() => import('@/pages/Register'))
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'))
const ResetPassword = lazy(() => import('@/pages/ResetPassword'))
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'))
const Profile = lazy(() => import('@/pages/Profile'))
const NotFound = lazy(() => import('@/pages/NotFound'))
const SettingsPage = lazy(() => import('@/pages/Settings'))
const DesignSystemPreview = lazy(() => import('@/pages/_devtools/DesignSystemPreview'))
const Terminal = lazy(() => import('@/terminal/Terminal'))
const ContrarianSignals = lazy(() => import('@/pages/ContrarianSignals'))
const ThematicResearch = lazy(() => import('@/pages/ThematicResearch'))
const PodcastIntelligence = lazy(() => import('@/pages/PodcastIntelligence'))
const SectorSkills = lazy(() => import('@/pages/SectorSkills'))
const ResearchInbox = lazy(() => import('@/pages/ResearchInbox'))
const PeerGroups = lazy(() => import('@/pages/PeerGroups'))
const DataPipelines = lazy(() => import('@/pages/DataPipelines'))

function Fallback() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="h-8 w-8 border-2 border-purple border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

export default function App() {
  useEffect(() => { preloadAllRoutes() }, [])

  return (
    <AuthProvider>
      <DevErrorBoundary>
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/:ticker" element={<Navigate to="quantitative" replace />} />
              <Route path="/dashboard/:ticker/quantitative" element={<DashboardLayout><QuantitativePage /></DashboardLayout>} />
              <Route path="/dashboard/:ticker/credit-risk" element={<DashboardLayout><CreditRiskPage /></DashboardLayout>} />
              <Route path="/dashboard/:ticker/insider-flow" element={<DashboardLayout><InsiderFlowPage /></DashboardLayout>} />
              <Route path="/dashboard/:ticker/governance" element={<DashboardLayout><GovernancePage /></DashboardLayout>} />
              <Route path="/dashboard/:ticker/event-timeline" element={<DashboardLayout><EventTimelinePage /></DashboardLayout>} />
              <Route path="/dashboard/:ticker/options-market" element={<DashboardLayout><OptionsMarketPage /></DashboardLayout>} />
              <Route path="/dashboard/:ticker/macro-overlay" element={<DashboardLayout><MacroOverlayPage /></DashboardLayout>} />
              <Route path="/dashboard/:ticker/attention-sentiment" element={<DashboardLayout><AttentionSentimentPage /></DashboardLayout>} />
              {/* Analysis nested routes: /analysis/:ticker auto-redirects to overview via index */}
              <Route path="/analysis/:ticker" element={<AnalysisShell />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<AnalysisOverviewTab />} />
                <Route path="financials" element={<AnalysisFinancialsTab />} />
                <Route path="valuation" element={<AnalysisValuationTab />} />
                <Route path="comps" element={<AnalysisCompsTab />} />
                <Route path="qualitative" element={<AnalysisQualitativeTab />} />
                <Route path="risk" element={<AnalysisRiskTab />} />
                <Route path="filings" element={<AnalysisFilingsTab />} />
              </Route>
              <Route path="/jobs/:jobId" element={<JobProgress />} />
              <Route path="/screen" element={<Screen />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/manual-input" element={<ManualInput />} />
              <Route path="/mna" element={<MNA />} />
              <Route path="/mna/results/:acquirer/:target" element={<MNAResults />} />
              <Route path="/analytics" element={<OwnerRoute><Analytics /></OwnerRoute>} />
              <Route path="/watchlist" element={<Watchlist />} />
              <Route path="/events" element={<Events />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/estimates" element={<Estimates />} />
              <Route path="/compliance" element={<OwnerRoute><Compliance /></OwnerRoute>} />
              <Route path="/pdf-import" element={<Navigate to="/manual-input" replace />} />
              <Route path="/devtools" element={<OwnerRoute><DevTools /></OwnerRoute>} />
              <Route path="/backtesting" element={<Backtesting />} />
              <Route path="/batch" element={<BatchAnalysis />} />
              <Route path="/factors" element={<FactorAnalytics />} />
              <Route path="/ml-insights" element={<MLInsights />} />
              <Route path="/strategies" element={<Strategies />} />
              <Route path="/moat" element={<Moat />} />
              <Route path="/moat/:ticker" element={<MoatDetail />} />
              <Route path="/credit-universe" element={<CreditUniverse />} />
              <Route path="/crowded-trades" element={<CrowdedTrades />} />
              <Route path="/calibration" element={<OwnerRoute><Calibration /></OwnerRoute>} />
              {/* Apophis v2 doc-grid routes */}
              <Route path="/apophis" element={<ApophisCatalog />} />
              <Route path="/apophis/new/:docId" element={<ApophisGenerate />} />
              <Route path="/apophis/jobs/:jobId" element={<ApophisJob />} />
              <Route path="/apophis/documents" element={<ApophisDocuments />} />
              {/* Apophis v1 routes (legacy tier-based flow) */}
              <Route path="/apophis/tiers" element={<Apophis />} />
              <Route path="/apophis/tiers/new/:tier" element={<ApophisRequest />} />
              <Route path="/apophis/deliveries/:jobId" element={<ApophisDelivery />} />
              <Route path="/apophis/history" element={<ApophisHistory />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<OwnerRoute><SettingsPage /></OwnerRoute>} />
              <Route path="/devtools/design-system-preview" element={<OwnerRoute><DesignSystemPreview /></OwnerRoute>} />
              <Route path="/terminal" element={<Terminal />} />
              <Route path="/contrarian" element={<ContrarianSignals />} />
              <Route path="/thematic" element={<ThematicResearch />} />
              <Route path="/podcasts" element={<PodcastIntelligence />} />
              <Route path="/sector-skills" element={<SectorSkills />} />
              <Route path="/research-inbox" element={<ResearchInbox />} />
              <Route path="/peer-groups" element={<PeerGroups />} />
              <Route path="/data-pipelines" element={<OwnerRoute><DataPipelines /></OwnerRoute>} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </DevErrorBoundary>
    </AuthProvider>
  )
}
